import { useEffect, useState, useRef } from "react";
import { useSearchParams, useLocation, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";
import api from "@/lib/api";
import { Section } from "@/components/common";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const loc = useLocation();
  const canceled = loc.pathname.includes("cancel");
  const study = loc.pathname.startsWith("/power-study");
  const sid = params.get("session_id");
  const [state, setState] = useState(canceled ? "canceled" : "polling");
  const tries = useRef(0);

  useEffect(() => {
    if (canceled || !sid) return;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (++tries.current > 20) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid, canceled]);

  const views = {
    polling: { icon: <Loader2 className="text-[#3B82F6] mx-auto mb-4 animate-spin" size={44} />, title: "Confirming your payment…", body: "Hang tight — we're verifying your payment with Stripe." },
    paid: { icon: <CheckCircle2 className="text-[#84CC16] mx-auto mb-4" size={48} />, title: "Payment successful", body: "Thank you! Your advertising order is confirmed and our team has been notified. No placement goes live until we approve it." },
    failed: { icon: <XCircle className="text-red-400 mx-auto mb-4" size={48} />, title: "Payment not completed", body: "Your payment didn't go through. You can try again from the pricing page." },
    timeout: { icon: <Clock className="text-amber-400 mx-auto mb-4" size={48} />, title: "Still processing", body: "This is taking longer than expected. If you completed payment, you'll receive confirmation shortly." },
    canceled: { icon: <XCircle className="text-slate-400 mx-auto mb-4" size={48} />, title: "Checkout canceled", body: "No charge was made. You can pick a package again whenever you're ready." },
  };
  if (study) {
    views.paid.body = "Your $1,500 deposit was received and your DYOR Power Feasibility Study order is now Received. A confirmation email is on its way. Track progress in your dashboard.";
    views.canceled.body = "No charge was made. You can return to the order form whenever you're ready.";
    views.failed.body = "Your payment didn't go through. You can try again from the order form.";
  }
  const v = views[state] || views.polling;
  const back = study ? (state === "paid" ? ["/account", "Go to my dashboard"] : ["/power-study", "Back to order form"]) : ["/advertise/pricing", "Back to pricing"];

  return (
    <Section className="py-24 max-w-lg text-center">
      <div className="card p-10" data-testid="payment-result">
        {v.icon}
        <h1 className="font-display font-bold text-2xl text-white">{v.title}</h1>
        <p className="text-slate-400 mt-2">{v.body}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
          <Link to={back[0]} data-testid="payment-result-primary" className="bg-[#2563EB] hover:bg-[#3B82F6] text-white font-semibold px-5 py-2.5 rounded-lg">{back[1]}</Link>
          <Link to="/" className="border border-white/15 text-white font-semibold px-5 py-2.5 rounded-lg">Home</Link>
        </div>
      </div>
    </Section>
  );
}
